import { Injector } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { fabric } from 'fabric';
import { ItemModel } from '../../../../models/itemModel';
import { FrameItem } from '../../../../models/session';
import { ResourceItemModel } from '../../../../models/resource';
import { VideoComponent } from '../../video/video.component';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { BoardItemComponentClass, FabricControls, MAX_BOARD_ITEMS_COUNT } from './board.item';
import { VideoConfigurations } from './board-video-configurations';
import { CreationContext } from './board-item-configurations';

// Hide all rotation controls and middle controls for videos
const VideoInvisibleControls = [
  FabricControls.MiddleTop,
  FabricControls.MiddleBottom,
  FabricControls.MiddleLeft,
  FabricControls.MiddleRight,
  FabricControls.RotateBottomLeft1,
  FabricControls.RotateBottomLeft2,
  FabricControls.RotateBottomRight1,
  FabricControls.RotateBottomRight2,
  FabricControls.RotateTopLeft1,
  FabricControls.RotateTopLeft2,
  FabricControls.RotateTopRight1,
  FabricControls.RotateTopRight2,
];

export class BoardVideo extends BoardItemComponentClass<VideoConfigurations, VideoComponent>(
  fabric.Rect,
) {
  AngularComponentClass = VideoComponent;

  private boardItemsInteractionsManagerService: BoardItemsInteractionsManager;

  private playModeSubscription?: Subscription;
  private videoReadySubscription?: Subscription;

  constructor(
    injector: Injector,
    configurations: VideoConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.Video, configurations, VideoInvisibleControls, creationContext);

    this.hasRotatingPoint = false;
    this.evented = false; // should only be interactable when the video is ready, not when in loading state

    this.boardItemsInteractionsManagerService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
  }

  protected override onDeselection(): void {
    if (this.componentRef?.instance) {
      this.componentRef.instance.disablePlayMode();
    }
  }

  bindAngularComponentOutputs() {
    super.bindAngularComponentOutputs();

    this.setUpPlayModeSubscription();
    this.setUpVideoReadySubscription();
  }

  protected onFabricRemoved() {
    super.onFabricRemoved();

    this.playModeSubscription?.unsubscribe();
    this.videoReadySubscription?.unsubscribe();
  }

  shouldEnterEditMode(mouseY: number, itemY: number, itemSelected: boolean): boolean {
    if (!this.componentRef) {
      return false;
    }
    // We consider that the video in the edit mode when the video is playing,
    // This means that any action keyboard/mouse input should be processed by the video only, hence we are in edit mode
    return itemSelected && this.componentRef.instance.playModeEnabled$.getValue();
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();
    return {
      ...encodedBoardItem,
      model: ItemModel.Resource,
      resource_state: {
        resource_url: this.configurations.url,
        resource_name: this.configurations.title,
        resource_item_model: ResourceItemModel.VIDEO,
      },
    };
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = false;
    this.lockScalingY = false;
    this.lockUniScaling = true;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = true;
  }

  getZIndexObservable$(): Observable<number> {
    // When the video is selected, it becomes above all other items.
    return combineLatest([
      this.boardItemsInteractionsManagerService.itemSelected$(this.configurations.uid),
      this.itemOrder$,
    ]).pipe(
      map(([itemSelected, itemOrder]) => {
        if (this.isOnStaticCanvas()) {
          return itemOrder;
        }
        if (itemSelected) {
          return MAX_BOARD_ITEMS_COUNT + 1;
        }
        const index = itemOrder - MAX_BOARD_ITEMS_COUNT - 1;
        return Math.min(-1, index);
      }),
      distinctUntilChanged(),
    );
  }

  setUpPlayModeSubscription() {
    if (!this.componentRef) {
      return;
    }

    this.playModeSubscription?.unsubscribe();
    this.playModeSubscription = this.componentRef.instance.playModeChanged.subscribe(
      (isPlaying: boolean) => {
        if (isPlaying) {
          this.set({
            hasControls: false,
            borderColor: 'red',
          } as Partial<this>);
        } else {
          this.set({
            hasControls: true,
            borderColor: '#0082f5',
          } as Partial<this>);
        }
        this.canvas?.requestRenderAll();
      },
    );
  }
  setUpVideoReadySubscription() {
    if (!this.componentRef) {
      return;
    }

    this.videoReadySubscription?.unsubscribe();
    this.videoReadySubscription = this.componentRef.instance.videoReady.subscribe(() => {
      this.evented = true;
    });
  }
}
